import {useIsFocused, useRoute} from '@react-navigation/native';
import decode from 'jwt-decode';
import {AuthTokensDto, UserLoginDto} from 'puffco-api-axios-client';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useAsync} from 'react-use';

import {Screens} from '../../constants/navigation';
import {Auth} from '../../contexts/auth';
import {useAsyncFn} from '../../lib/hooks/useAsyncFn';
import type {RootStackScreenProps} from '../../navigators/RootStackNavigator';
import {optionsToRoute, toHome} from '../../navigators/util';
import {analytics} from '../../services/analytics';
import {authApi} from '../api/apis';
import {updateAppSettings} from '../redux/slices/appSettingsSlice';
import {setUser, userSelector} from '../redux/slices/userSlice';
import {AppSettingsStore} from '../redux/types';
import {User} from '../types';
import NavigationService from '../utils/NavigationService';

const decodeUser = (message: any): User => ({
  id: message.sub,
  firstName: message.firstName,
  lastName: message.lastName,
  username: message.username,
  defaultUsername: message.defaultUsername,
  email: message.email,
  created: message.created,
  modified: '',
  roles: message.roles,
  pushNotifications: message.pushNotifications,
  verified: message.verified,
  marketOptIn: message.marketOptIn,
  enableBugfender: message.enableBugfender,
});

const decodeAppSettings = (message: any): Partial<AppSettingsStore> => ({
  tempPreference: message.tempPreference,
  enablePeakAnimation: message.enablePeakAnimation,
  batteryPreservationOn: message.batteryPreservation,
});

const decodeAccessToken = (accessToken: string) => {
  const message: any = decode(accessToken);

  return {user: decodeUser(message), appSettings: decodeAppSettings(message)};
};

type ScreenProps = RootStackScreenProps<
  typeof Screens.Login | typeof Screens.Register
>;

type Route = ScreenProps['route'];

export const useLogin = () => {
  const dispatch = useDispatch();
  const {createSession} = Auth.useContainer();

  return React.useCallback(
    async ({email, password}: UserLoginDto) => {
      const {
        data: {accessToken, refreshToken},
      } = await authApi.login({userLoginDto: {email, password}});

      await createSession({accessToken, refreshToken});

      const {user, appSettings} = decodeAccessToken(accessToken);

      dispatch(setUser(user));
      dispatch(updateAppSettings(appSettings));

      return user;
    },
    [createSession],
  );
};

const useSocialLogin = (
  method: 'apple' | 'google',
  callback: (code: string) => Promise<AuthTokensDto>,
) => {
  const dispatch = useDispatch();
  const route = useRoute<Route>();
  const {createSession} = Auth.useContainer();

  return useAsyncFn(
    async (code: string) => {
      const {accessToken, refreshToken, newUser} = await callback(code);

      analytics.trackEvent(newUser ? 'sign up' : 'login', {method});

      const {redirect} = route.params ?? {};

      await createSession({accessToken, refreshToken});

      const {user, appSettings} = decodeAccessToken(accessToken);

      dispatch(setUser(user));

      dispatch(updateAppSettings(appSettings));

      NavigationService.instance()?.resetRoot({
        index: 0,
        routes: [optionsToRoute(redirect ?? toHome)],
      });

      return user;
    },
    [createSession, route.params],
  );
};

export const useAppleLogin = () => {
  const loggedIn = !!useSelector(userSelector);
  const isFocused = useIsFocused();
  const route = useRoute<Route>();
  const code = route?.params?.appleCode;

  const [state, login] = useSocialLogin('apple', (code: string) =>
    authApi
      .appleAuth({userAppleAuthDto: {code, legacy: false}})
      .then(r => r.data),
  );

  useAsync(async () => {
    if (!code || loggedIn || !isFocused) return;

    login(code).catch();
  }, [code, isFocused, loggedIn, login]);

  return [state, login] as const;
};

export const useGoogleLogin = () => {
  const loggedIn = !!useSelector(userSelector);
  const isFocused = useIsFocused();
  const route = useRoute<Route>();
  const code = route?.params?.googleCode;

  const [state, login] = useSocialLogin('google', (code: string) =>
    authApi.googleAuth({userGoogleAuthDto: {code}}).then(r => r.data),
  );

  useAsync(async () => {
    if (!code || loggedIn || !isFocused) return;

    login(code).catch();
  }, [code, isFocused, loggedIn, login]);

  return [state, login] as const;
};
